import type { Action, ActionCreator } from 'redux';
import type { ThunkAction } from 'redux-thunk';
import { differenceInHours } from 'date-fns';
import { SET_UNREAD_MESSAGES_COUNT } from '@gumtree/shared/src/constants/message-centre';
import { MessageUserRoleEnum } from '@gumtree/web-bff/src/api/clients/conversations';
import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { listingDetailsFromMessageCentre } from '@gumtree/shared/src/util/ga4-bff';
import type {
    Conversation,
    ConversationsMap,
    Message,
    MessageCentreInit,
} from '@gumtree/shared/src/types/message-centre';
import { SellerType } from '@gumtree/shared/src/types/advertising/partnership';

import { MessageCentreState, MessageCentreThunk } from './index';
import {
    getConversations,
    getConversation,
    postMessage,
    putMessagesAsRead,
    blockUser as blockUserRequest,
    unblockUser as unblockUserRequest,
    deleteConversation as deleteConversationRequest,
    reportUser as reportUserRequest,
    getCanReportConversation,
    getCanUserReview,
    createReview,
    getPaginatedConversations,
    postBrazeNotification,
    recordReviewDelay,
    judgeReviewDelay,
} from '../get-data/api';
import getUnreadMessagesCount from '../utils/unread-messages-count';
import {
    SendMessagePayload,
    MarkMessagesAsReadPayload,
    BlockUserPayload,
    DeleteConversationPayload,
    FetchConversationPayload,
    ReportUserPayload,
    CanReportConversationPayload,
    CanReviewPayload,
} from './types';
import logger from '../logger';
import {
    trackBlockUserBegin,
    trackBlockUserFailure,
    trackBlockUserSuccess,
    trackDeleteConversation,
    trackErrorFetchActiveConversations,
    trackErrorFetchConversations,
    trackMessageSendAttempt,
    trackMessageSendFailure,
    trackMessageSendSuccess,
    trackReportUser,
    trackUnblockUserBegin,
    trackUnblockUserSuccess,
} from '../utils/tracking';
import { getUserRole, mergeConversations } from './utils';
import { CONVERSATIONS_PAGE_LIMIT } from '../constants';
import { hasEachParticipantSentMinMessageCount } from '../components/utils/has-each-participant-sent-min-message';

export const FETCH_CONVERSATIONS_SUCCESS = 'FETCH_CONVERSATIONS_SUCCESS';
export const FETCH_PAGINATED_CONVERSATIONS_SUCCESS = 'FETCH_PAGINATED_CONVERSATIONS_SUCCESS';
export const FETCH_PAGINATED_CONVERSATIONS_FAILURE = 'FETCH_PAGINATED_CONVERSATIONS_FAILURE';
export const SET_ACTIVE_CONVERSATION_ID = 'SET_ACTIVE_CONVERSATION_ID';
export const SEND_MESSAGE_START = 'SEND_MESSAGE_START';
export const SEND_MESSAGE_END = 'SEND_MESSAGE_END';
export const UPDATE_CONVERSATIONS = 'UPDATE_CONVERSATIONS';
export const UPDATE_CONVERSATION = 'UPDATE_CONVERSATION';
export const SET_MESSAGE_TEXT = 'SET_MESSAGE_TEXT';
export const MARK_AS_READ = 'MARK_AS_READ';
export const SET_IS_OPEN_MOBILE_MODAL_CHAT = 'SET_IS_OPEN_MOBILE_MODAL_CHAT';
export const DELETE_CONVERSATION = 'DELETE_CONVERSATION';
export const SET_IS_LOADING = 'SET_IS_LOADING';
export const UPDATE_CAN_REPORT_CONVERSATION = 'UPDATE_GET_REPORT_CONVERSATION';
export const ADD_LAST_MESSAGE = 'ADD_LAST_MESSAGE';
export const MARK_MESSAGES_AS_NOT_DELIVERED = 'MARK_MESSAGES_AS_NOT_DELIVERED';
export const CAN_USER_REVIEW = 'CAN_USER_REVIEW';
export const SET_DELAY_REVIEW_TIME = 'SET_DELAY_REVIEW_TIME';
export const SET_GRANULAR_RESPONSE = 'SET_GRANULAR_RESPONSE';
export const SET_GRANULAR_POSITIVE_RESPONSE = 'SET_GRANULAR_POSITIVE_RESPONSE';
export const SET_GRANULAR_NEGATIVE_RESPONSE = 'SET_GRANULAR_NEGATIVE_RESPONSE';
export const CLEAR_GRANULAR_RESPONSE = 'CLEAR_GRANULAR_RESPONSE';
export const CLEAR_NEGATIVE_POSITIVE_RESPONSE = 'CLEAR_NEGATIVE_POSITIVE_RESPONSE';
export const CLOSE_REVIEW_DIALOG = 'CLOSE_REVIEW_DIALOG';
export const FETCH_RATING_SUCCESS = 'FETCH_RATING_SUCCESS';
export const IS_REVIEW_SUCCESS = 'IS_REVIEW_SUCCESS';
export const IS_REVIEW_ERROR = 'IS_REVIEW_ERROR';
export const SET_IS_LOADING_NEXT_PAGE = 'SET_IS_LOADING_NEXT_PAGE';
export const SET_SELECTED_STARS = 'SET_SELECTED_STARS';
export const SET_REFRESH_REQUIRED = 'SET_REFRESH_REQUIRED';
export const SET_HARD_STOP_REFRESH_CONVERSATIONS = 'SET_HARD_STOP_REFRESH_CONVERSATIONS';
export const SET_REVIEW_MODAL_OPEN = 'SET_REVIEW_MODAL_OPEN';
export const SET_IMAGE_ERROR = 'SET_IMAGE_ERROR';

export const updateConversations = (messageCentre: MessageCentreInit) => ({
    type: UPDATE_CONVERSATIONS,
    payload: { messageCentre },
});

export const fetchConversationsSuccess = (conversations: ConversationsMap) => ({
    type: FETCH_CONVERSATIONS_SUCCESS,
    payload: { conversations },
});

export const fetchPaginatedConversationsSuccess = (
    conversations: ConversationsMap,
    hasMore: boolean
) => ({
    type: FETCH_PAGINATED_CONVERSATIONS_SUCCESS,
    payload: { conversations, hasMore },
});

export const setHardStopRefreshConversations = (isStop: boolean) => ({
    type: SET_HARD_STOP_REFRESH_CONVERSATIONS,
    payload: { isStop },
});

export const setRefreshRequired = (isRefreshRequired: boolean) => ({
    type: SET_REFRESH_REQUIRED,
    payload: { isRefreshRequired },
});

export const fetchPaginatedConversationsFailure = () => ({
    type: FETCH_PAGINATED_CONVERSATIONS_FAILURE,
});

export const updateConversation = (conversation: Conversation) => ({
    type: UPDATE_CONVERSATION,
    payload: { conversation },
});

export const setReviewModalOpen = (reviewModalOpen: boolean) => ({
    type: SET_REVIEW_MODAL_OPEN,
    payload: { reviewModalOpen },
});

export const setActiveConversationId = (conversationId: string) => ({
    type: SET_ACTIVE_CONVERSATION_ID,
    payload: { id: conversationId },
});

export const sendMessageStart = () => ({
    type: SEND_MESSAGE_START,
});

export const sendMessageEnd = () => ({
    type: SEND_MESSAGE_END,
});

export const setMessageText = (messageText: string) => ({
    type: SET_MESSAGE_TEXT,
    payload: { messageText },
});

export const setImageError = (imageError: boolean, imageErrorType: string) => ({
    type: SET_IMAGE_ERROR,
    payload: { imageError, imageErrorType },
});

export const markAsRead = (conversationId: string) => ({
    type: MARK_AS_READ,
    payload: { conversationId },
});

export const setIsOpenMobileModalChat = (isOpen: boolean) => ({
    type: SET_IS_OPEN_MOBILE_MODAL_CHAT,
    payload: { isOpen },
});

export const deleteConversationSuccess = (conversationId: string) => ({
    type: DELETE_CONVERSATION,
    payload: { conversationId },
});

export const setIsLoading = (isLoading: boolean) => ({
    type: SET_IS_LOADING,
    payload: { isLoading },
});

export const updateCanReportConversation = (canReport: boolean | null) => ({
    type: UPDATE_CAN_REPORT_CONVERSATION,
    payload: { canReport },
});

export const addLastMessage = (payload: { message: Partial<Message>; conversationId: string }) => ({
    type: ADD_LAST_MESSAGE,
    payload,
});

export const markMessageAsNotDelivered = (payload: {
    messageId: string;
    conversationId: string;
}) => ({
    type: MARK_MESSAGES_AS_NOT_DELIVERED,
    payload,
});

export const setUnreadMessagesCount = (unreadMessagesCount = 0) => ({
    type: SET_UNREAD_MESSAGES_COUNT,
    payload: unreadMessagesCount,
});

export const setIsLoadingNextPage = (isLoading: boolean) => ({
    type: SET_IS_LOADING_NEXT_PAGE,
    payload: { isLoading },
});

/** used only for polling purpose */
export const fetchConversations: MessageCentreThunk<null> = () => async (dispatch, getState) => {
    const {
        baseConfig: { bffUrl },
        messageCentre,
    } = getState();

    try {
        const conversations = await getConversations(bffUrl);
        const { mergedConversations, hasNewConversations } = mergeConversations({
            existingConversations: messageCentre.conversations,
            conversations,
            appendIfNotPresent: false,
        });
        const totalUnreadMessagesCount = getUnreadMessagesCount(Object.values(mergedConversations));

        /** some conversations could be missed during pagination due to stream api,
         * if polling request receives new conversations then refresh call is required
         * note: refresh requires an action from user, we don't do it automatically to reduce load on stream */
        if (
            hasNewConversations &&
            conversations.length === CONVERSATIONS_PAGE_LIMIT &&
            totalUnreadMessagesCount > 0
        ) {
            dispatch(setRefreshRequired(true));
        }

        dispatch(setUnreadMessagesCount(totalUnreadMessagesCount));
        dispatch(fetchConversationsSuccess(mergedConversations));
    } catch (e) {
        trackErrorFetchConversations(e);
        logger.error('client: message-centre/fetchConversations', e);
    }
};

export function sortConversationsByUpdatedAt(conversations: Conversation[]): ConversationsMap {
    return conversations
        .sort((a, b) => {
            const dateA = new Date(a.updatedAt).getTime();
            const dateB = new Date(b.updatedAt).getTime();
            return dateB - dateA;
        })
        .reduce((acc, conversation) => {
            acc[conversation.id] = conversation;
            return acc;
        }, {});
}

export const refreshConversations: MessageCentreThunk<boolean> =
    (shouldShowLoading) => async (dispatch, getState) => {
        const {
            baseConfig: { bffUrl },
            messageCentre,
        } = getState();

        shouldShowLoading && dispatch(setIsLoading(true));

        try {
            const { conversations } = await getPaginatedConversations(bffUrl);
            const { mergedConversations } = mergeConversations({
                existingConversations: messageCentre.conversations,
                conversations,
                appendIfNotPresent: true,
            });
            const totalUnreadMessagesCount = getUnreadMessagesCount(
                Object.values(mergedConversations)
            );

            const unsortedConversations: Conversation[] = conversations;
            const sortedConversations: ConversationsMap =
                sortConversationsByUpdatedAt(unsortedConversations);

            dispatch(setUnreadMessagesCount(totalUnreadMessagesCount));
            dispatch(fetchConversationsSuccess(sortedConversations));
        } catch (e) {
            dispatch(setHardStopRefreshConversations(true));
            trackErrorFetchConversations(e);
            logger.error('client: message-centre/refreshConversations', e);
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export const fetchConversationsWithDetails: MessageCentreThunk<number> =
    (pageNumber) => async (dispatch, getState) => {
        const {
            baseConfig: { bffUrl },
            messageCentre,
        } = getState();

        dispatch(setIsLoadingNextPage(true));

        try {
            const { conversations, hasMore } = await getPaginatedConversations(bffUrl, pageNumber);
            const { mergedConversations } = mergeConversations({
                existingConversations: messageCentre.conversations,
                conversations,
                appendIfNotPresent: true,
            });
            const totalUnreadMessagesCount = getUnreadMessagesCount(
                Object.values(mergedConversations)
            );

            dispatch(fetchPaginatedConversationsSuccess(mergedConversations, hasMore));
            dispatch(setUnreadMessagesCount(totalUnreadMessagesCount));
        } catch (e) {
            trackErrorFetchConversations(e);
            dispatch(fetchPaginatedConversationsFailure());
            logger.error('client: message-centre/fetchConversationsWithDetails', e);
        } finally {
            dispatch(setIsLoadingNextPage(false));
        }
    };

export const canUserReview = (canReview: boolean) => ({
    type: CAN_USER_REVIEW,
    payload: { canReview },
});

export const setDelayReviewTime = (reviewDelayTime: boolean) => ({
    type: SET_DELAY_REVIEW_TIME,
    payload: { reviewDelayTime },
});



export const onGranularResponse = (granularResponse: string) => ({
    type: SET_GRANULAR_RESPONSE,
    payload: { granularResponse },
});

export const onGranularResponsePositive = (granularResponse: string) => ({
    type: SET_GRANULAR_POSITIVE_RESPONSE,
    payload: { granularResponse },
});

export const onGranularResponseNegative = (granularResponse: string) => ({
    type: SET_GRANULAR_NEGATIVE_RESPONSE,
    payload: { granularResponse },
});

export const onGranularResponseClear = () => ({
    type: CLEAR_GRANULAR_RESPONSE,
});

export const onSelectedStars = (starNumber: number) => ({
    type: SET_SELECTED_STARS,
    payload: { starNumber },
});

export const onCombinedGranularResponseClear = () => ({
    type: CLEAR_NEGATIVE_POSITIVE_RESPONSE,
});

export const closeReviewDialog = () => ({
    type: CLOSE_REVIEW_DIALOG,
});

export const isReviewSuccess = (review: boolean) => ({
    type: IS_REVIEW_SUCCESS,
    payload: { reviewSuccess: review },
});

export const isReviewError = (isError: boolean) => ({
    type: IS_REVIEW_ERROR,
    payload: { reviewError: isError },
});

export const fetchCanUserReview: MessageCentreThunk = () => async (dispatch, getState) => {
    const {
        baseConfig: { bffUrl },
        messageCentre: { activeConversationId, conversations },
        userData: { id: userId },
    } = getState();
    const activeConversation = conversations[activeConversationId!] as Conversation;

    if (activeConversation) {
        const {
            adId,
            sellerId,
            buyerId,
            id,
            advert: { categoryId, l2CategoryId, supportShipping, sellerType },
        } = activeConversation;

        if (supportShipping) {
            dispatch(canUserReview(false));
            return;
        }

        const revieweeUserId = userId === sellerId ? buyerId : sellerId;
        try {
            const canReview = await getCanUserReview(
                {
                    adId,
                    reviewerUserId: userId,
                    revieweeUserId,
                    conversationId: id,
                    l1CategoryId: categoryId,
                    l2CategoryId,
                    sellerType: sellerType as SellerType,
                },
                bffUrl
            );

            dispatch(canUserReview(canReview));
        } catch (e) {
            logger.error('client: message-centre/can-user-review', e);
        }
    }
};

export const fetchRecordReviewDelay: MessageCentreThunk = () => async (_dispatch, getState) => {
    const {
        baseConfig: { bffUrl },
        messageCentre: { activeConversationId, conversations },
        userData: { id: userId },
    } = getState();
    try {
        const activeConversation = conversations[activeConversationId!] as Conversation;
        const {
            sellerId,
            buyerId,
        } = activeConversation;

        const revieweeUserId = userId === sellerId ? buyerId : sellerId;
        const { delayHours } = await recordReviewDelay(
            {
                conversationId: activeConversationId,
                reviewerUserId: userId,
                revieweeUserId,
            },
            bffUrl
        );
        return delayHours;
    } catch (e) {
        logger.error('client: message-centre/fetchRecordReviewDelay', e);
        return undefined;
    }
};

export const fetchJudgeReviewDelay: MessageCentreThunk = () => async (dispatch, getState) => {
    const {
        baseConfig: { bffUrl },
        messageCentre: { activeConversationId, conversations },
        userData: { id: userId },
    } = getState();
    const activeConversation = conversations[activeConversationId!] as Conversation;
    const {
        sellerId,
        buyerId,
    } = activeConversation;
    try {
        const revieweeUserId = userId === sellerId ? buyerId : sellerId;
        const delayReviewTime = await judgeReviewDelay(
            {
                conversationId: activeConversationId,
                reviewerUserId: userId,
                revieweeUserId,
            },
            bffUrl
        );
        dispatch(setDelayReviewTime(delayReviewTime));
    } catch (e) {
        logger.error('client: message-centre/fetchJudgeReviewDelay', e);
    }
};

export const setBrazeNotificationWithReviewee: MessageCentreThunk<number> =
    (rating: number) => async (_dispatch, getState) => {
        const {
            baseConfig: { bffUrl },
            messageCentre: { activeConversationId, conversations },
            userData: { id: userId, firstName: userName },
        } = getState();
        const activeConversation = conversations[activeConversationId!];

        if (activeConversation) {
            const {
                adId,
                sellerId,
                buyerId,
                id,
                advert: { categoryId, l2CategoryId, sellerType},
                conversee,
            } = activeConversation;

            const reviewerUserId = userId === sellerId ? buyerId : sellerId;
            const reviewerUserName = conversee.firstName;
            const revieweeUserId = userId;
            const revieweeUserName = userName;
            try {
                const canReview = await getCanUserReview(
                    {
                        adId,
                        reviewerUserId,
                        revieweeUserId,
                        conversationId: id,
                        l1CategoryId: categoryId,
                        l2CategoryId,
                        sellerType: sellerType as SellerType,
                    } as CanReviewPayload,
                    bffUrl
                );
                const param = {
                    adId: String(adId),
                    conversationId: id,
                    reviewerUserId: String(revieweeUserId),
                    reviewerUserName: revieweeUserName,
                    revieweeUserId: String(reviewerUserId),
                    revieweeUserName: reviewerUserName,
                    canReverseReview: canReview,
                    direction: revieweeUserId === sellerId ? 'S2B' : 'B2S',
                    status: 'completed',
                    rating,
                };
                await postBrazeNotification(param, bffUrl);
            } catch (e) {
                logger.error('client: message-centre/set-braze-notification-reviewee', e);
            }
        }
    };

export const markMessagesAsRead: MessageCentreThunk<MarkMessagesAsReadPayload> =
    (payload: MarkMessagesAsReadPayload) => async (dispatch, getState) => {
        const {
            baseConfig: { bffUrl },
            messageCentre: { conversations },
            unreadMessagesCount,
        } = getState();

        try {
            await putMessagesAsRead(payload, bffUrl);

            const readMessagesCount = conversations[payload.conversationId]?.unreadMessagesCount;

            dispatch(setUnreadMessagesCount(unreadMessagesCount - readMessagesCount));
            dispatch(markAsRead(payload.conversationId));
        } catch (e) {
            logger.error('client: message-centre/markMessagesAsRead', e);
        }
    };

export const postReview: MessageCentreThunk<number> = (rating) => async (dispatch, getState) => {
    const {
        baseConfig: { bffUrl, isQA },
        messageCentre: {
            activeConversationId,
            conversations,
            granularResponse,
            positiveResponse,
            negativeResponse,
        },
    } = getState();
    const activeConversation = conversations[activeConversationId!] as Conversation;
    const {
        adId,
        sellerId,
        buyerId,
        id,
        advert: { categoryId, title },
    } = activeConversation;

    let failed = false;
    let joinedFeedback = [] as string[];

    try {
        await createReview(
            {
                adId,
                sellerId,
                buyerId,
                id,
                categoryId,
                title,
                granularResponse,
                rating,
                combinedFeedback: false,
                positiveFeedback: positiveResponse,
                negativeFeedback: negativeResponse,
                isQA,
                source: 'conversation', // The fixed form marks this as the entry for comments initiated by conversation
            },
            bffUrl
        );
        dispatch(isReviewSuccess(true));
        dispatch(setBrazeNotificationWithReviewee(rating));

        trackV2('RateUserSuccess', `${rating} ${rating > 1 ? 'stars' : 'star'}`);

        if (granularResponse.length > 0 && (rating === 1 || rating === 5)) {
            trackV2('UserFeedbackSuccess', granularResponse.join(';').replaceAll(' ', ''));
        }

        if (positiveResponse.length > 0 || negativeResponse.length > 0) {
            const joined = [...positiveResponse, ...negativeResponse];
            trackV2('UserFeedbackSuccess', joined.join(';').replaceAll(' ', ''));
            joinedFeedback = [...positiveResponse, ...negativeResponse];
        }
    } catch (e) {
        dispatch(isReviewError(true));
        logger.error('client: message-centre/can-user-review', e);
        failed = true;
    }

    const isConverseeBuyer = activeConversation.conversee.userId === activeConversation.buyerId;
    trackGA4Event<GA4.ConversationRateUser>({
        event: 'rate_a_user',
        formValidation: failed ? 'failure' : 'success',
        rateAUserStars: rating,
        rateAUserTags: joinedFeedback,
        rateAUserDirection: isConverseeBuyer ? 'S2B' : 'B2S',
        ad_id: activeConversation.adId,
        ad_name: activeConversation.advert.title,
        reviewee_userId: String(
            isConverseeBuyer ? activeConversation.sellerId : activeConversation.buyerId
        ),
    });
};

export const fetchConversation = async (payload: FetchConversationPayload, bffUrl) => {
    try {
        return await getConversation(payload, bffUrl);
    } catch (e) {
        trackErrorFetchActiveConversations(e);
        logger.error('client: message-centre/fetchConversation', e);
        throw e;
    }
};

export const checkMessageForReview: MessageCentreThunk = () => async (dispatch, getState) => {
    const {
        userData: { id },
        messageCentre: { activeConversationId, conversations, canReview },
    } = getState();
    const activeConversation = conversations[activeConversationId!] as Conversation;
    const { messages } = activeConversation;
    // Trigger can review check when messages === 5

    if (
        messages.length === 5 &&
        hasEachParticipantSentMinMessageCount(messages, id) &&
        !canReview
    ) {
        dispatch(fetchCanUserReview());
    }
};

export const checkStreamMessageForReview: MessageCentreThunk<any[]> =
    (streamMessage) => async (dispatch, getState) => {
        const {
            userData: { id },
            messageCentre: { canReview },
        } = getState();

        if (
            streamMessage?.length === 5 &&
            hasEachParticipantSentMinMessageCount(streamMessage, id) &&
            !canReview
        ) {
            dispatch(fetchCanUserReview());
        }
    };

export function trackMessageSend(
    {
        messageCentre: { activeConversationId, conversations },
        userData: { id: userId },
    }: Pick<MessageCentreState, 'messageCentre' | 'userData'>,
    type: 'success' | 'failure'
) {
    const activeConversation = conversations[activeConversationId!];
    const userRole = getUserRole({ activeConversationId, userId, conversations });
    const messageDirection =
        userRole === MessageUserRoleEnum.Seller ? 'seller to buyer' : 'buyer to seller';
    const lastParticipantMessage = activeConversation.messages
        .slice()
        .reverse()
        .find((message) => message.userRole !== userRole);
    const contactParticipantMessageTimeLag = lastParticipantMessage
        ? differenceInHours(Date.now(), new Date(lastParticipantMessage.createdAt))
        : undefined;

    if (type === 'success') {
        trackMessageSendSuccess({
            messageTimeLag: contactParticipantMessageTimeLag,
            messageDirection,
            ...listingDetailsFromMessageCentre(activeConversation),
        });
    } else {
        trackMessageSendFailure({
            messageTimeLag: contactParticipantMessageTimeLag,
            messageDirection,
            ...listingDetailsFromMessageCentre(activeConversation),
        });
    }
}

export const sendMessage: MessageCentreThunk<SendMessagePayload> =
    (payload: SendMessagePayload) => async (dispatch, getState) => {
        const {
            baseConfig: { bffUrl },
            messageCentre: { activeConversationId, conversations },
            userData: { firstName, lastName, email, id: userId },
        } = getState();

        const activeConversation = conversations[activeConversationId!] as Conversation;
        const userRole = getUserRole({ activeConversationId, userId, conversations });

        const ad = {
            title: activeConversation?.advert?.title,
            imageUrl: activeConversation?.advert?.img,
        };

        const metaData = {
            ad,
            firstName,
            lastName,
            email,
            userAgent: navigator.userAgent,
            categoryId: activeConversation.advert.categoryId,
        };

        dispatch(sendMessageStart());
        trackMessageSendAttempt();

        const message: Partial<Message> = {
            /** temporary fake id */
            id: Date.now().toString(),
            text: payload.messageBody,
            userRole,
            userId,
            updatedAt: Date.now(),
        };

        try {
            dispatch(setMessageText(''));
            dispatch(addLastMessage({ message, conversationId: activeConversationId!! }));

            await postMessage({ ...payload, metaData }, bffUrl);

            trackMessageSend(getState(), 'success');

            dispatch(checkMessageForReview());
        } catch (e) {
            dispatch(
                markMessageAsNotDelivered({
                    messageId: message.id!!,
                    conversationId: activeConversationId!!,
                })
            );

            trackMessageSend(getState(), 'failure');

            logger.error('client: message-centre/sendMessage', e);
        } finally {
            dispatch(sendMessageEnd());
        }

        try {
            const conversation = await fetchConversation(payload, bffUrl);
            dispatch(updateConversation(conversation));
        } catch (e) {
            logger.error('client: message-centre/sendMessage - update conversation', e);
        }
    };

export const blockUser: MessageCentreThunk<BlockUserPayload> =
    (payload: BlockUserPayload) => async (dispatch, getState) => {
        const {
            baseConfig: { bffUrl },
            messageCentre: { conversations: existingConversations, activeConversationId },
            userData: { id: userId },
        } = getState();

        const userRole = getUserRole({
            conversations: existingConversations,
            activeConversationId,
            userId,
        });

        const activeConversation = existingConversations[activeConversationId!];

        try {
            dispatch(setIsLoading(true));

            trackBlockUserBegin();

            await blockUserRequest(payload, bffUrl);

            trackBlockUserSuccess(userRole, activeConversation);

            const conversations = await getConversations(bffUrl);
            const { mergedConversations } = mergeConversations({
                existingConversations,
                conversations,
                appendIfNotPresent: false,
            });

            dispatch(fetchConversationsSuccess(mergedConversations));
        } catch (e) {
            trackBlockUserFailure(userRole, activeConversation);
            logger.error('client: message-centre/blockUser', e);
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export const unblockUser: MessageCentreThunk<BlockUserPayload> =
    (payload: BlockUserPayload) => async (dispatch, getState) => {
        const {
            baseConfig: { bffUrl },
            messageCentre,
        } = getState();

        try {
            dispatch(setIsLoading(true));

            trackUnblockUserBegin();
            await unblockUserRequest(payload, bffUrl);
            trackUnblockUserSuccess();

            const conversations = await getConversations(bffUrl);
            const { mergedConversations } = mergeConversations({
                existingConversations: messageCentre.conversations,
                conversations,
                appendIfNotPresent: false,
            });

            dispatch(fetchConversationsSuccess(mergedConversations));
        } catch (e) {
            logger.error('client: message-centre/unblockUser', e);
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export const deleteConversation: MessageCentreThunk<DeleteConversationPayload> =
    (payload: DeleteConversationPayload) => async (dispatch, getState) => {
        const {
            messageCentre: { conversations, activeConversationId },
            baseConfig: { bffUrl },
            userData: { id: userId },
        } = getState();

        const userRole = getUserRole({
            conversations,
            activeConversationId,
            userId,
        });

        const activeConversation = conversations[activeConversationId!];
        const activeConversationDetails = listingDetailsFromMessageCentre(activeConversation);
        const conversationId = activeConversation.id;

        try {
            dispatch(setIsLoading(true));
            await deleteConversationRequest(payload, bffUrl);
            dispatch(deleteConversationSuccess(payload.conversationId));
            trackDeleteConversation({
                userRole,
                ...activeConversationDetails,
                conversationId,
            });
        } catch (e) {
            trackDeleteConversation({
                userRole,
                success: false,
                ...listingDetailsFromMessageCentre(activeConversation),
                conversationId,
            });
            logger.error('client: message-centre/deleteConversation', e);
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export const canReportConversation: MessageCentreThunk<CanReportConversationPayload> =
    (payload: CanReportConversationPayload) => async (dispatch, getState) => {
        const {
            baseConfig: { bffUrl },
        } = getState();

        try {
            /** disables report functionality until we won't get response */
            dispatch(updateCanReportConversation(null));

            const canReportUser = await getCanReportConversation(payload, bffUrl);

            dispatch(updateCanReportConversation(canReportUser));
        } catch (e) {
            logger.error('client: message-centre/can-report-conversation', e);
        }
    };

export const reportUser: MessageCentreThunk<ReportUserPayload> =
    (payload: ReportUserPayload) => async (dispatch, getState) => {
        const {
            baseConfig: { bffUrl },
            messageCentre: { conversations, activeConversationId },
            userData: { id: userId },
        } = getState();

        const userRole = getUserRole({
            conversations,
            activeConversationId,
            userId,
        });

        const { adId, sellerId, buyerId } = conversations[activeConversationId!!];
        const converseeId = userId === sellerId ? buyerId : sellerId;

        const context = {
            advertId: adId,
            conversationId: activeConversationId!!,
            converseeId,
            userId,
        };

        const activeConversation = conversations[activeConversationId!];

        try {
            await reportUserRequest({ ...payload, context }, bffUrl);
            dispatch(canReportConversation({ conversationId: activeConversationId!!, userId }));

            trackReportUser({
                userRole,
                reasons: payload.reasons,
                converseeId,
                ...listingDetailsFromMessageCentre(activeConversation),
            });
        } catch (e) {
            trackReportUser({
                userRole,
                success: false,
                reasons: payload.reasons,
                converseeId,
                ...listingDetailsFromMessageCentre(activeConversation),
            });
            logger.error('client: message-centre/reportUser', e);
        }
    };

export const getMessageCentreStateThunk: ActionCreator<
    ThunkAction<MessageCentreState, MessageCentreState, void, Action>
> = () => {
    return (_dispatch, getState) => getState();
};
